import React, { useState, useEffect } from "react";
import "./Home.css";

import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Box, // Used for layout and spacing
  Grid, // Used for responsive layout
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles"; // Import useTheme
import { useSelector, useDispatch } from "react-redux";
import AddRecipe from "./AddRecipeModal";
import EditRecipe from "./EditRecipeModal";
import { loadRecipes, resetRecipes } from "../actions/recipes";
import { mockRecipes } from "../mockData/recipes";
import AddIcon from "@mui/icons-material/Add";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import MenuIcon from "@mui/icons-material/Menu";

// Removed: import { makeStyles } from "@mui/styles";
// Removed: useStyles definition

// Default image URL for recipes without a medium image
const DEFAULT_IMAGE_URL =
  "https://www.foodiesfeed.com/wp-content/uploads/2019/06/top-view-for-box-of-2-burgers-home-made.jpg";

function Home() {
  const navigate = useNavigate();
  const recipes = useSelector((state) => state.recipes);
  const dispatch = useDispatch();
  const theme = useTheme(); // Access the theme
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [open, setOpen] = useState(false);
  const [openForEdit, setOpenForEdit] = useState(false);
  const [currentRecipe, setCurrentRecipe] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    // Load mock data if recipe list is empty
    if (!recipes || !recipes.recipesList || recipes.recipesList.length === 0) {
      dispatch(loadRecipes(mockRecipes.recipes));
    }
  }, [dispatch, recipes]);

  // --- Handlers ---
  const handleClickOpen = () => setOpen(true);
  const handleClickEditOpen = (tile) => {
    setCurrentRecipe(tile);
    setOpenForEdit(true);
  };
  const handleClose = () => setOpen(false);
  const handleEditClose = () => setOpenForEdit(false);

  const handleNavigateDirectionsClick = (foodItem) => {
    navigate(`/directions/${foodItem.uuid}`);
  };

  const handleNavigateIngredientsClick = (foodItem) => {
    navigate(`/ingredients/${foodItem.uuid}`);
  };

  const reset = () => {
    dispatch(resetRecipes());
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // --- Styling Objects (Replaces useStyles) ---
  const headerContainerSx = {
    flexGrow: 1,
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
  };

  const navButtonContainerSx = {
    display: "flex",
    gap: theme.spacing(1), // Space between buttons
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  };

  const cardSx = {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxShadow: theme.shadows[5],
    transition: "transform 0.2s",
    "&:hover": {
      transform: "scale(1.02)",
    },
  };

  const mediaSx = {
    height: 250,
  };

  return (
    <React.Fragment>
      <AppBar position="static">
        <Toolbar sx={headerContainerSx}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            component="div"
            sx={{ fontWeight: 600 }}
          >
            🥘 Taste Genius
          </Typography>
          <Box sx={navButtonContainerSx}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RestaurantIcon />}
              onClick={() => navigate("/specials")}
              sx={{ color: "#fff" }}
            >
              View Specials
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleClickOpen}
              sx={{ color: "#fff" }}
            >
              Add Recipe
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={reset}
            >
              Reset Recipes
            </Button>
          </Box>
          {isMobile && (
            <IconButton edge="end" color="inherit" onClick={handleMenuOpen}>
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            navigate("/specials");
            handleMenuClose();
          }}
        >
          <RestaurantIcon sx={{ mr: 1 }} />
          Specials
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClickOpen();
            handleMenuClose();
          }}
        >
          <AddIcon sx={{ mr: 1 }} />
          Add Recipe
        </MenuItem>
        <MenuItem
          onClick={() => {
            reset();
            handleMenuClose();
          }}
        >
          <DeleteForeverIcon />
          Reset Recipes
        </MenuItem>
      </Menu>
      <AddRecipe open={open} onClose={handleClose} />
      <EditRecipe
        currentRecipe={currentRecipe}
        open={openForEdit}
        onClose={handleEditClose}
      />

      <Box sx={{ padding: theme.spacing(4) }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
        >
          Available Recipes
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {recipes.recipesList &&
            recipes.recipesList.map((tile) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={tile.uuid}>
                <Card sx={cardSx}>
                  <CardMedia
                    sx={mediaSx}
                    image={tile.images?.medium || DEFAULT_IMAGE_URL}
                    title={tile.title}
                    component="img"
                  />

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {tile.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="p"
                      sx={{ mb: 1 }}
                    >
                      {tile.description}
                    </Typography>

                    <Box
                      sx={{
                        mt: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        pt: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.primary">
                        **Servings:** {tile.servings}
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        **Prep Time:** {tile.prepTime} min
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        **Cook Time:** {tile.cookTime} min
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Posted: {tile.postDate}
                      </Typography>
                      {!!tile.editDate && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          Edited: {tile.editDate}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>

                  <CardActions
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider}`,
                      justifyContent: "space-between",
                      padding: theme.spacing(1),
                    }}
                  >
                    <Button
                      onClick={() => handleNavigateDirectionsClick(tile)}
                      size="small"
                      color="primary"
                    >
                      Directions
                    </Button>
                    <Button
                      onClick={() => handleNavigateIngredientsClick(tile)}
                      size="small"
                      color="primary"
                    >
                      Ingredients
                    </Button>
                    <Button
                      onClick={() => handleClickEditOpen(tile)}
                      size="small"
                      color="secondary"
                    >
                      Edit
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
        </Grid>
      </Box>
    </React.Fragment>
  );
}

export default Home;
